import React from "react";
import PropTypes from "prop-types";
import { Form } from "formik";

// Material
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import IconButton from "@material-ui/core/IconButton";
import LinearProgress from "@material-ui/core/LinearProgress";

// Icons
import backIcon from "../../assets/icons/icons-back.svg";

// Config
import styles from "../../config/styles";
import useTranslate from "../../hooks/useTranslate";

// Components
import Button from "../Button";
import Text from "../Text";

export const FIRST_STEP = 1;
export const TOTAL_STEPS = 4;

const useStyles = makeStyles(theme => ({
  root: {
    border: styles.values.border,
    maxWidth: 998,
    margin: "0 auto"
  },
  header: {
    padding: theme.spacing(2, 3),
    borderBottom: styles.values.border,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between"
  },
  titleGroup: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  title: {
    textAlign: "left",
    fontSize: 22
  },
  progressWrapper: {
    padding: theme.spacing(2.5)
  },
  progress: {
    height: 10,
    borderRadius: 5
  },
  progressTrack: {
    backgroundColor: "#F7F7FA"
  },
  progressBar: {
    borderRadius: 5,
    background:
      "transparent linear-gradient(90deg, #3ACCE1 0%, #14FF65 100%) 0% 0% no-repeat padding-box"
  },
  progressLabel: {
    marginTop: theme.spacing(1),
    fontFamily: ["Gibson Regular", "sans-serif"].join(","),
    fontSize: 12,
    textAlign: "left"
  },
  progressLabelCurrent: {
    fontFamily: ["Gibson SemiBold", "sans-serif"].join(",")
  },
  body: {
    padding: theme.spacing(2.5, 2.5, 0)
  },
  footer: {
    padding: theme.spacing(1, 2.5, 4),
    display: "flex",
    justifyContent: "flex-start"
  },
  submitButton: {
    minWidth: 96
  }
}));

const LAST_STEP = FIRST_STEP + TOTAL_STEPS - 1;

const WizardStepCard = ({
  banner,
  children,
  disableSubmit,
  hideBack,
  hideProgress,
  hideSkip,
  isSubmitting,
  onBack,
  onSkip,
  step,
  title
}) => {
  const classes = useStyles();
  const getText = useTranslate();
  const progressValue = ((step - FIRST_STEP + 1) / TOTAL_STEPS) * 100;
  const isLastStep = step === LAST_STEP;

  return (
    <Paper elevation={0} className={classes.root}>
      <Form>
        <div className={classes.header}>
          <div className={classes.titleGroup}>
            {!hideBack && (
              <IconButton onClick={onBack}>
                <img src={backIcon} alt="" />
              </IconButton>
            )}
            <Text className={classes.title} text={title} type="heading2" />
          </div>
          {!hideSkip && (
            <Button disabled={isSubmitting} onClick={onSkip}>
              {getText("common.prompts.skip")}
            </Button>
          )}
        </div>
        {!hideProgress && (
          <div className={classes.progressWrapper}>
            <LinearProgress
              classes={{
                bar: classes.progressBar,
                colorPrimary: classes.progressTrack
              }}
              className={classes.progress}
              value={progressValue}
              variant="determinate"
            />
            <div className={classes.progressLabel}>
              <span className={classes.progressLabelCurrent}>{step}</span>
              {`/${LAST_STEP}`}
            </div>
          </div>
        )}
        {banner}
        <div className={classes.body}>{children}</div>
        <div className={classes.footer}>
          <Button
            className={classes.submitButton}
            color="primary"
            disabled={isSubmitting || disableSubmit}
            disableElevation
            type="submit"
            variant="contained"
          >
            {getText(
              isLastStep ? "common.prompts.done" : "common.prompts.next"
            )}
          </Button>
        </div>
      </Form>
    </Paper>
  );
};

WizardStepCard.propTypes = {
  banner: PropTypes.node,
  children: PropTypes.node.isRequired,
  disableSubmit: PropTypes.bool,
  hideBack: PropTypes.bool,
  hideProgress: PropTypes.bool,
  hideSkip: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  onBack: PropTypes.func,
  onSkip: PropTypes.func,
  step: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired
};

WizardStepCard.defaultProps = {
  banner: null,
  disableSubmit: false,
  hideBack: false,
  hideProgress: false,
  hideSkip: false,
  isSubmitting: false,
  onBack: () => {},
  onSkip: () => {}
};

export default WizardStepCard;
